import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from 'react';
import queueosLogo from "@/assets/queueos-icon-loading.png";

// Critical routes loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import PublicForm from "./pages/PublicForm";

// Lazy load non-critical routes
const Student = lazy(() => import("./pages/Student"));
const DeAnza = lazy(() => import("./pages/DeAnza"));
const AffiliateLanding = lazy(() => import("./pages/AffiliateLanding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Organization = lazy(() => import("./pages/Organization"));
const Queues = lazy(() => import("./pages/Queues"));
const PublicForms = lazy(() => import("./pages/PublicForms"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Apps = lazy(() => import("./pages/Apps"));
const AppStore = lazy(() => import("./pages/AppStore"));
const CanvasSync = lazy(() => import("./pages/CanvasSync"));
const ClassroomSync = lazy(() => import("./pages/ClassroomSync"));
const MyOrganization = lazy(() => import("./pages/MyOrganization"));
const Webhooks = lazy(() => import("./pages/Webhooks"));
const AppointmentOS = lazy(() => import("./pages/AppointmentOS"));
const CalendarSubscription = lazy(() => import("./pages/CalendarSubscription"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const NewInviteSignup = lazy(() => import("./pages/NewInviteSignup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivateBeta = lazy(() => import("./pages/PrivateBeta"));
const QueueOS = lazy(() => import("./pages/QueueOS"));
const AppLauncherPage = lazy(() => import("./pages/AppLauncher"));
const Documents = lazy(() => import("./pages/Documents"));
const DocumentEditor = lazy(() => import("./pages/DocumentEditor"));
const WorkspacePermissions = lazy(() => import("./pages/WorkspacePermissions"));
const AI = lazy(() => import("./pages/AI"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

import CannyIntegration from "./components/CannyIntegration";
import { MessengerWidget } from "./components/MessengerWidget";
import { StickyNotes } from "./components/StickyNotes";
import { PostHogRouterTracker } from "./components/analytics/PostHogRouterTracker";
import { PostHogIdentify } from "./components/analytics/PostHogIdentify";
import { ErrorBoundary } from "./components/analytics/ErrorBoundary";
import { PostHogTestRunner } from "./components/analytics/PostHogTestRunner";
import { AuthProvider } from "./contexts/AuthContext";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { WorkspaceTabsProvider } from "./contexts/WorkspaceTabsContext";

const WorkspaceLayout = lazy(() => import("./components/WorkspaceLayout").then(m => ({ default: m.WorkspaceLayout })));
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { useThemeSync } from "./hooks/useThemeSync";

const RecoveryRedirect = () => {
  const navigate = useNavigate();
  useThemeSync(); // Sync theme from database
  
  useEffect(() => {
    // Fix encoded query strings in pathname (e.g., /queues%3Fqueue= should be /queues?queue=)
    const pathname = window.location.pathname;
    if (pathname.includes('%3F') || pathname.includes('%3f')) {
      const decodedPath = decodeURIComponent(pathname);
      // Split the decoded path at the first ? to get pathname and search
      const questionIndex = decodedPath.indexOf('?');
      if (questionIndex !== -1) {
        const correctPathname = decodedPath.substring(0, questionIndex);
        const correctSearch = decodedPath.substring(questionIndex);
        navigate(`${correctPathname}${correctSearch}`, { replace: true });
        return;
      }
    }
    
    const hash = window.location.hash;
    const search = window.location.search;
    const hasRecovery = (hash && hash.includes('type=recovery')) || (search && search.includes('type=recovery'));
    if (hasRecovery && window.location.pathname !== '/reset-password') {
      const suffix = hash || search || '';
      navigate(`/reset-password${suffix}`, { replace: true });
    }
  }, [navigate]);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CannyIntegration />
      
<AuthProvider>
        <ImpersonationProvider>
          <WorkspaceTabsProvider>
            <BrowserRouter>
            <ImpersonationBanner />
            <RecoveryRedirect />
            <PostHogRouterTracker />
            <PostHogIdentify />
            <PostHogTestRunner />
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
                  <img src={queueosLogo} alt="queueOS" className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8" />
                </div>
              </div>
            }>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/deanza" element={<DeAnza />} />
        <Route path="/student" element={<Student />} />
        <Route path="/affiliate/:slug" element={<AffiliateLanding />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/signupsuccess" element={<SignupSuccess />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/new-invite-signup" element={<NewInviteSignup />} />
        <Route path="/oauth/consent" element={<OAuthConsent />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/quinn" element={<AI />} />
        <Route path="/dashboard/org/:organizationId" element={<WorkspaceLayout />} />
        <Route path="/dashboard/org/:organizationId/home" element={<WorkspaceLayout />} />
        <Route path="/dashboard/org/:organizationId/queues" element={<Queues />} />
        <Route path="/dashboard/org/:organizationId/apps" element={<Apps />} />
        <Route path="/dashboard/org/:organizationId/integration-store" element={<AppStore />} />
        <Route path="/dashboard/org/:organizationId/canvas-sync" element={<CanvasSync />} />
        <Route path="/dashboard/org/:organizationId/classroom-sync" element={<ClassroomSync />} />
          <Route path="/dashboard/org/:organizationId/forms" element={<PublicForms />} />
          <Route path="/dashboard/org/:organizationId/analytics" element={<Analytics />} />
          <Route path="/dashboard/org/:organizationId/organization" element={<MyOrganization />} />
            <Route path="/dashboard/org/:organizationId/webhooks" element={<Webhooks />} />
            <Route path="/dashboard/org/:organizationId/appointmentos" element={<AppointmentOS />} />
            <Route path="/dashboard/org/:organizationId/calendar-subscription" element={<CalendarSubscription />} />
            <Route path="/dashboard/org/:organizationId/permissions" element={<WorkspacePermissions />} />
            <Route path="/dashboard/org/:organizationId/documents" element={<Documents />} />
            <Route path="/dashboard/org/:organizationId/documents/:documentId" element={<DocumentEditor />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/private-beta" element={<PrivateBeta />} />
            <Route path="/whyqueueos" element={<QueueOS />} />
            <Route path="/app-launcher" element={<AppLauncherPage />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/documents/:documentId" element={<DocumentEditor />} />
        <Route path="/forms/:slug" element={<PublicForm />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
            </Suspense>
      <StickyNotes />
      <MessengerWidget />
            </BrowserRouter>
          </WorkspaceTabsProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </TooltipProvider>
    </ErrorBoundary>
);

export default App;
